import {
  Box,
  Tooltip,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material"
import type React from "react"
import { extraColors } from "../../utils/theme"
import {
  NwssDetectionHeatmapTooltip,
  nwssHeatmapTooltipSlotProps,
} from "./NwssDetectionHeatmapTooltip"
import { DrawerChartTitle } from "./precomputedDrawerStyles"
import { NwssEventWindowLegend } from "./NwssEventWindowLegend"
import {
  weekOverlapsNwssEventWindow,
  type NwssEventWindow,
} from "./nwssEventWindow"
import type { NwssDrawerSampleScope } from "./nwssDisplayFormat"
import type { NwssSignalSample } from "./nwssSampleTypes"
import { NWSS_DRAWER_CHART_LOOKBACK_MONTHS } from "./nwssWeeklyCharts"
import { NwssWeeklyChartFrame } from "./NwssWeeklyTimelineHeader"
import { NWSS_WEEK_GRID_ROW_LABEL_WIDTH_PX } from "./nwssWeeklyGridLayout"
import type { NwssHeatmap } from "./useNwssSignalSamples"

export const heatmapCellColor = (count: number, max: number): string =>
  count === 0
    ? extraColors.translucent5
    : `rgba(255, 75, 85, ${0.25 + 0.75 * (count / max)})`

export interface NwssDetectionHeatmapProps {
  heatmap: NwssHeatmap
  samples: NwssSignalSample[]
  groupBy: "site" | "county"
  rowNoun: "County" | "Site"
  sectionTitle: string
  reportingCounties?: string[] | null
  lookbackMonths?: number
  eventWindow?: NwssEventWindow | null
  sampleScope?: NwssDrawerSampleScope
}

export const NwssDetectionHeatmap: React.FC<NwssDetectionHeatmapProps> = ({
  heatmap,
  samples,
  groupBy,
  rowNoun,
  sectionTitle,
  reportingCounties = null,
  lookbackMonths = NWSS_DRAWER_CHART_LOOKBACK_MONTHS,
  eventWindow = null,
  sampleScope = "historic",
}) => {
  const { sites: rows, weekKeys, weekLabels } = heatmap
  if (rows.length === 0 || weekKeys.length === 0) return null

  return (
    <Box sx={{ width: "100%" }}>
      <DrawerChartTitle>{sectionTitle}</DrawerChartTitle>
      <HeatmapCaption lookbackMonths={lookbackMonths} />
      {sampleScope === "historic" && eventWindow && (
        <NwssEventWindowLegend eventWindow={eventWindow} />
      )}
      <Box sx={{ maxHeight: 280, overflow: "auto", width: "100%" }}>
        <HeatmapGrid
          heatmap={heatmap}
          samples={samples}
          groupBy={groupBy}
          rowNoun={rowNoun}
          rows={rows}
          weekKeys={weekKeys}
          weekLabels={weekLabels}
          reportingCounties={reportingCounties}
          lookbackMonths={lookbackMonths}
          eventWindow={eventWindow}
          showEventWindowBand={
            sampleScope === "historic" && eventWindow != null
          }
        />
      </Box>
    </Box>
  )
}

interface HeatmapCaptionProps {
  lookbackMonths: number
}

const HeatmapCaption: React.FC<HeatmapCaptionProps> = ({ lookbackMonths }) => (
  <Typography
    variant="caption"
    sx={{ color: extraColors.translucent50, display: "block", mb: 1 }}
  >
    {`Last ${lookbackMonths} months · weekly detection count (darker = more).`}
  </Typography>
)

interface HeatmapGridProps {
  heatmap: NwssHeatmap
  samples: NwssSignalSample[]
  groupBy: "site" | "county"
  rowNoun: "County" | "Site"
  rows: string[]
  weekKeys: string[]
  weekLabels: string[]
  reportingCounties: string[] | null
  lookbackMonths: number
  eventWindow: NwssEventWindow | null
  showEventWindowBand: boolean
}

const HeatmapGrid: React.FC<HeatmapGridProps> = ({
  heatmap,
  samples,
  groupBy,
  rowNoun,
  rows,
  weekKeys,
  weekLabels,
  reportingCounties,
  lookbackMonths,
  eventWindow,
  showEventWindowBand,
}) => {
  const weekTrackSx: SxProps<Theme> = {
    flex: 1,
    minWidth: 0,
    display: "grid",
    gridTemplateColumns: `repeat(${weekKeys.length}, minmax(0, 1fr))`,
    gridTemplateRows: `repeat(${rows.length}, auto)`,
    gap: "2px",
    alignItems: "center",
  }

  return (
    <NwssWeeklyChartFrame
      weekKeys={weekKeys}
      eventWindow={eventWindow}
      showEventWindowBand={showEventWindowBand}
    >
      <Box sx={{ display: "flex", gap: "2px", width: "100%" }}>
        <Box
          sx={{
            width: NWSS_WEEK_GRID_ROW_LABEL_WIDTH_PX,
            flexShrink: 0,
            display: "grid",
            gridTemplateRows: `repeat(${rows.length}, auto)`,
            gap: "2px",
            alignItems: "center",
          }}
        >
          {rows.map(row => (
            <Typography
              key={row}
              sx={{
                fontSize: "0.65rem",
                color: extraColors.translucent50,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
              title={`${rowNoun} ${row}`}
            >
              {row}
            </Typography>
          ))}
        </Box>
        <Box sx={weekTrackSx}>
          {rows.map(row =>
            weekKeys.map((week, index) => (
              <HeatmapCell
                key={`${row}__${week}`}
                row={row}
                weekKey={week}
                weekLabel={weekLabels[index] ?? ""}
                count={heatmap.cells.get(`${row}__${week}`) ?? 0}
                maxCount={heatmap.maxCount}
                groupBy={groupBy}
                samples={samples}
                reportingCounties={reportingCounties}
                lookbackMonths={lookbackMonths}
                inEventWindow={weekOverlapsNwssEventWindow(week, eventWindow)}
              />
            ))
          )}
        </Box>
      </Box>
    </NwssWeeklyChartFrame>
  )
}

interface HeatmapCellProps {
  row: string
  weekKey: string
  weekLabel: string
  count: number
  maxCount: number
  groupBy: "site" | "county"
  samples: NwssSignalSample[]
  reportingCounties: string[] | null
  lookbackMonths: number
  inEventWindow: boolean
}

const HeatmapCell: React.FC<HeatmapCellProps> = ({
  row,
  weekKey,
  weekLabel,
  count,
  maxCount,
  groupBy,
  samples,
  reportingCounties,
  lookbackMonths,
  inEventWindow,
}) => (
  <Tooltip
    arrow
    placement="top"
    slotProps={nwssHeatmapTooltipSlotProps}
    title={
      <NwssDetectionHeatmapTooltip
        row={row}
        weekKey={weekKey}
        weekLabel={weekLabel}
        groupBy={groupBy}
        samples={samples}
        reportingCounties={reportingCounties}
        lookbackMonths={lookbackMonths}
        inEventWindow={inEventWindow}
      />
    }
  >
    <Box
      sx={{
        width: "100%",
        aspectRatio: "1",
        maxHeight: 14,
        borderRadius: "2px",
        bgcolor: heatmapCellColor(count, maxCount),
      }}
    />
  </Tooltip>
)
